import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, HStack, InputGroup, Text } from "@chakra-ui/react";
import useApi from "hooks/useApi";
import { HttpStatusCode } from "axios";
import Pagination from "components/pagination/Pagination";
import ErrorDisplay from "components/errorDisplay/ErrorDisplay";
import { OverlayLoaderComponent } from "components/loader/Loader";
import SidebarFilter from "components/sidebarFilter/SidebarFilter";
import ManageColumns from "components/manageColumns/ManageColumns";
import NoDataFound from "components/noDataFound/NoDataFound";
import CustomTable from "components/table/CustomTable";
import { FiChevronsDown, FiFilter } from "react-icons/fi";
import { recommendationColumns } from "./variables";
import { SearchBar } from "components/navbar/searchBar/SearchBar";
import { PAYMENT_LIST } from "constants/endpoints";
import { PAYMENT_STATUS } from "constants/filterDropdownData";
import { SUBSCRIPTION_CREATED_BY } from "constants/filterDropdownData";
const PAGE_SIZE = 50;

export default function PaymentList() {
  const [isOpenFilterSidebar, setIsOpenFilterSidebar] = useState(false);
  const [leadsData, setLeadsData] = useState([]);
  const { apiCall, loading = true, error } = useApi();
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const [filters, setFilters] = useState({});

  const [totalPages, setTotalPages] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const today = new Date();
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(today.getDate() - 3);

  const [manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox, setManageColumnSelectAllCheckbox] =
    useState(false);
  const toggleSidebar = () => {
    setIsOpenFilterSidebar(!isOpenFilterSidebar);
  };

  const fetchLeadsData = useCallback(
    async (providedUserText) => {
      try {
        const { created_by } = filters;
        const body = {
          ...filters,
          created_by: created_by?.join(","),
          search_text: providedUserText || searchText || "",
          page: currentPage,
          limit: pageSize,
        };
        const res = await apiCall.get(PAYMENT_LIST, body);
        if (res?.statusCode === HttpStatusCode.Ok) {
          const data = res.data.list || [];
          const totalCount = res.data.count;
          setLeadsData(data);
          setTotalPages(Math.ceil(totalCount / pageSize));
          setTotalCount(totalCount);
        }
      } catch (error) {
        console.log("Error while fetching plans: ", error.message);
      }
    },
    [currentPage, pageSize, filters]
  );

  const columns = useMemo(() => recommendationColumns, []);
  const data = useMemo(() => leadsData, [leadsData]);

  const excludedColumnVisibility = ["_id"];
  const [columnVisibility, setColumnVisibility] = useState(
    columns.reduce((acc, column) => {
      acc[column.accessor] = !excludedColumnVisibility.includes(
        column.accessor
      );
      return acc;
    }, {})
  );

  const handleRemoveAllFilter = () => {
    setFilters({});
  };

  const dropdownFilters = [
    {
      name: 'status',
      placeholder: 'Select Status',
      value: filters?.status,
      data: PAYMENT_STATUS,
    },
    {
      name: 'created_by',
      placeholder: 'Created By',
      value: filters?.created_by,
      data: SUBSCRIPTION_CREATED_BY,
    },
  ];

  const allFiltersProps = {
    dropdowns: dropdownFilters,
  };

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    setCurrentPage(1);
  }, []);

  const handleColumnChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  const visibleColumns = columns.filter(
    (column) => columnVisibility[column.accessor]
  );

  const handleApplyFilter = () => {
    setCurrentPage(1);
    fetchLeadsData();
  };

  useEffect(() => {
    fetchLeadsData();
  }, [currentPage, pageSize]);

  const setStartDate = (text) => {
    setFilters((prev) => {
      return { ...prev, start_date: text };
    });
  };

  const setEndDate = (text) => {
    setFilters((prev) => {
      return { ...prev, end_date: text };
    });
  };

  const handleInputFormSubmit = (e) => {
    e.preventDefault();

    setCurrentPage(1);
    fetchLeadsData(searchText);
  };

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <>
      <Box height="100vh" bg="#F8FAFB">
        <Flex
          justify="space-between"
          align="center"
          p="2"
          boxShadow="sm"
          borderRadius="sm"
          pos="fixed"
          style={{ width: "calc(100vw - 96px)" }}
          zIndex={1000}
        >
          <HStack spacing="4" p={2}>
            <Button
              paddingX={6}
              leftIcon={<FiFilter />}
              rightIcon={<FiChevronsDown />} // Changed the icon to ChevronDown
              colorScheme="teal"
              variant="outline"
              borderRadius="md"
              size="sm"
              onClick={() => {
                toggleSidebar();
              }}
            >
              Filter
            </Button>
            <ManageColumns
              columns={columns}
              handleColumnChange={handleColumnChange}
              columnVisibility={columnVisibility}
              setManageColumnSelectAllCheckbox={
                setManageColumnSelectAllCheckbox
              }
              manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox={manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox}
            />
            <form onSubmit={handleInputFormSubmit}>
              <InputGroup size="sm">
                <SearchBar
                  background="#F9F9FB"
                  color="#637381"
                  setSearchText={(e) => setSearchText(e.target.value)}
                  searchText={searchText}
                  borderRadius="5px"
                  borderWidth="0.5px"
                  borderColor="#637381"
                />
              </InputGroup>
            </form>
          </HStack>
          <Text
            borderRadius="5px"
            borderWidth="0.5px"
            borderColor="#637381"
            background="#F9F9FB"
            color="#637381"
            p={1}
          >
            Payments: {totalCount}
          </Text>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(value) => {
              setCurrentPage(value);
            }}
            totalCount={totalCount}
            setCurrentPage={setCurrentPage}
            setPageSize={setPageSize}
            pageSize={pageSize}
          />
        </Flex>
        <SidebarFilter
          filters={allFiltersProps}
          handleFilterChange={handleFilterChange}
          handleApplyFilter={handleApplyFilter}
          isOpen={isOpenFilterSidebar}
          onClose={toggleSidebar}
          appliedFilters={filters}
          setFilters={setFilters}
          handleRemoveAllFilter={handleRemoveAllFilter}
          isCalenderFilter={true}
          startDate={filters?.start_date}
          setStartDate={setStartDate}
          endDate={filters?.end_date}
          setEndDate={setEndDate}
          enableMultiselect
          multiSelectKey={["created_by"]}
        />
        {leadsData?.length !== 0 ? (
          <CustomTable
            columns={visibleColumns}
            data={data}
            showSelect={false}
          />
        ) : !loading ? (
          <NoDataFound />
        ) : null}
        <OverlayLoaderComponent isOpen={loading} />
      </Box>
    </>
  );
}
